import React, { useState } from 'react';
import {
    Edit,
    SimpleForm,
    ReferenceInput,
    SelectInput,
    TextInput,
    DateInput,
    BooleanInput,
    required,
    useNotify,
    useRedirect,
    useRefresh,
} from 'react-admin';
import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import feathersClient from '../rest/feathersClient';
import settings from '../settings';

const useStyles = makeStyles(theme => ({
    fileInput: {
        display: 'none',
    },
    uploadButton: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
}));

const UserDocumentEdit = (props: any) => {
    const classes = useStyles();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                notify('Please upload a PDF or JPEG file', { type: 'warning' });
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                notify('File size must be less than 10MB', { type: 'warning' });
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleSave = async (values: any) => {
        try {
            const formData = new FormData();
            formData.append('userId', String(values.userId));
            formData.append('documentType', values.documentType);
            if (values.documentNumber) {
                formData.append('documentNumber', values.documentNumber);
            }
            if (values.expiryDate) {
                formData.append('expiryDate', values.expiryDate);
            }
            if (values.description) {
                formData.append('description', values.description);
            }
            if (values.isVerified !== undefined) {
                formData.append('isVerified', String(values.isVerified));
            }
            if (selectedFile) {
                formData.append('file', selectedFile);
            }

            const token = await feathersClient.authentication.getAccessToken();
            const response = await fetch(
                `${settings.apiUrl}/user-documents/${values.id}`,
                {
                    method: 'PATCH',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.message || 'Failed to update document'
                );
            }

            notify('Document updated successfully', { type: 'success' });
            redirect('show', '/user-documents', values.id);
            refresh();
        } catch (error) {
            console.error('Error updating document:', error);
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Error updating document';
            notify(errorMessage, { type: 'error' });
        }
    };

    return (
        <Edit {...props}>
            <SimpleForm save={handleSave}>
                <ReferenceInput
                    source="userId"
                    reference="users"
                    validate={required()}
                    label="User"
                    disabled
                >
                    <SelectInput optionText="name" />
                </ReferenceInput>

                <ReferenceInput
                    source="documentType"
                    reference="document-types"
                    validate={required()}
                    label="Document Type"
                    filter={{ isActive: true }}
                >
                    <SelectInput optionText="name" />
                </ReferenceInput>

                <TextInput
                    source="documentNumber"
                    label="Document Number"
                    fullWidth
                />

                <DateInput source="expiryDate" label="Expiry Date" />

                <TextInput
                    source="description"
                    label="Description"
                    multiline
                    rows={3}
                    fullWidth
                />

                <BooleanInput source="isVerified" label="Verified" />

                <Box mt={2}>
                    <input
                        accept="application/pdf,image/jpeg,image/jpg"
                        className={classes.fileInput}
                        id="document-file-upload"
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="document-file-upload">
                        <Button
                            variant="outlined"
                            component="span"
                            className={classes.uploadButton}
                        >
                            {selectedFile
                                ? `Selected: ${selectedFile.name}`
                                : 'Upload New PDF or JPEG (Optional)'}
                        </Button>
                    </label>
                    {selectedFile && (
                        <Typography variant="caption" display="block">
                            Size: {(selectedFile.size / 1024).toFixed(2)} KB
                        </Typography>
                    )}
                </Box>
            </SimpleForm>
        </Edit>
    );
};

export default UserDocumentEdit;
